"use strict";

import {getPrefixedDataSet} from '@elements/data-set-utils';
import {onFind} from "@elements/init-modules-in-scope";
import {findIn, on, findAll, hasClass} from "@elements/dom-utils";

const defaultOptions = {
    video: false,
    animateThumb: false,
    download: false,
    counter: false,
    closeOnBgTap: true
};

const defaultSelectors = {
    base: '.js-lightbox',
    item: '.js-lightbox__item'
};

export function init(options = defaultOptions, selectors = defaultSelectors) {
    console.log('Lightbox init');
    onFind(selectors.base, function (baseElement) {
        createLightbox(
            baseElement,
            {...defaultOptions, ...options},
            {...defaultSelectors, ...selectors}
        );
    });
}

export function createLightbox(lightbox, options = defaultOptions, selectors = defaultSelectors) {

    let lightboxItems = findIn(selectors.item, lightbox);

    let elementOptions = {
        ...defaultOptions,
        ...options,
        ...getPrefixedDataSet('lightbox', lightbox)
    };

    // Store the element that had focus before opening the lightbox
    let previouslyFocusedElement = null;

    return import('lightgallery.js').then(function () {

        if (elementOptions.video) {
            /* the following imports are only needed if there are videos in your lightGallery*/
            /* IMPORTANT: Do not forget to add 'lg-video-object lg-html5' to video-elements*/
            /* IMPORTANT: The controls attribute must be set on the <video>*/

            import('vimeo-froogaloop2');
            import('lg-video.js');
        }

        lightGallery(lightbox, {
            ...elementOptions,
            selector: selectors.item,
        });

        //prevent isClickable
        const lightboxClickHandler = evt => {
            evt.stopImmediatePropagation();
        };

        if (lightboxItems){
            on('click', lightboxClickHandler, lightboxItems);
        }

        if (options.closeOnBgTap){
            lightbox.addEventListener('onAfterOpen', function(event){
                closeGalleryOnBgTap(event);
            }, false);

            lightbox.addEventListener('onAfterSlide', function(event){
                closeGalleryOnBgTap(event);
            }, false);
        }

        // Add focus trapping
        lightbox.addEventListener('onAfterOpen', function(event) {
            // Store the element that had focus before opening the lightbox
            previouslyFocusedElement = document.activeElement;

            // Set up focus trapping
            setupFocusTrap(event);
        }, false);

        // Restore focus when lightbox closes
        lightbox.addEventListener('onBeforeClose', function() {
            // Restore focus to the element that had focus before the lightbox was opened
            if (previouslyFocusedElement) {
                previouslyFocusedElement.focus();
            }
        }, false);

        return window.lgData[lightbox.getAttribute('lg-uid')];
    });

}

function closeGalleryOnBgTap(event){
    let imgWrapItems = findAll('.lg-img-wrap');

    imgWrapItems.forEach((item) => {
        on('touchstart', (evt) => {
            if(!hasClass('lg-image',evt.target)){
                window.lgData[event.target.getAttribute('lg-uid')].destroy(false);
            }
        }, item);
    });
}

function setupFocusTrap(event) {
    // Get the lightbox container
    const lightboxUid = event.target.getAttribute('lg-uid');
    // Find the specific lightbox container for this instance
    let lightboxContainer = document.querySelector(`.lg-outer[data-uid="${lightboxUid}"]`);

    if (!lightboxContainer) {
        // Fallback to the first .lg-outer if we can't find the specific one
        lightboxContainer = document.querySelector('.lg-outer');
        if (!lightboxContainer) return;
    }

    // Find all focusable elements within the lightbox
    const focusableElements = getFocusableElements(lightboxContainer);

    if (focusableElements.length === 0) return;

    // Focus on the first element
    setTimeout(() => {
        focusableElements[0].focus();
    }, 50);

    // Add keydown event listener to handle tab key
    const handleTabKey = function(e) {
        // If tab key is pressed
        if (e.key === 'Tab' || e.keyCode === 9) {
            const activeElement = document.activeElement;
            const focusableElements = getFocusableElements(lightboxContainer);

            // If there are no focusable elements, do nothing
            if (focusableElements.length === 0) return;

            // Get the index of the current element
            const currentIndex = focusableElements.indexOf(activeElement);

            // If shift key is also pressed, go to the previous element
            if (e.shiftKey) {
                // If we're at the first element, loop to the last
                if (currentIndex === 0 || currentIndex === -1) {
                    e.preventDefault();
                    focusableElements[focusableElements.length - 1].focus();
                }
            } else {
                // If we're at the last element, loop to the first
                if (currentIndex === focusableElements.length - 1 || currentIndex === -1) {
                    e.preventDefault();
                    focusableElements[0].focus();
                }
            }
        }
    };

    // Add the event listener to the document
    document.addEventListener('keydown', handleTabKey);

    // Store the handler so we can remove it later
    lightboxContainer._tabKeyHandler = handleTabKey;

    // Remove the event listener when the lightbox is closed
    event.target.addEventListener('onBeforeClose', function() {
        if (lightboxContainer._tabKeyHandler) {
            document.removeEventListener('keydown', lightboxContainer._tabKeyHandler);
            delete lightboxContainer._tabKeyHandler;
        }
    }, { once: true });
}

function getFocusableElements(container) {
    // Selectors for all focusable elements
    const focusableElementsString = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable]';

    // Get all focusable elements within the container
    return Array.from(container.querySelectorAll(focusableElementsString))
        .filter(el => {
            // Filter out elements that are not visible
            return el.offsetWidth > 0 && el.offsetHeight > 0;
        });
}
